import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../hooks";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import {
  fetchPolicyUpdatesListAsync,
  getPolicyUpdateShareCode,
} from "../../../../../../redux/policyCommon/policyCommonActions";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { LoadState, MODULE_NAMES } from "../../../../../../constants/enums";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import { IPolicyUpdates } from "../../../../../../redux/policyCommon/policyCommon.types";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import { Button } from "@mui/material";
import { Add, ShareOutlined } from "@mui/icons-material";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import { PolicyUpdatesRightPanel } from "./PolicyUpdatesRightPanel";
import { EmailComposeDialog } from "../../../../../../components/MessagesDialogs/EmailCompose/EmailComposeDialog";
import { getEmailUpdateTemplate } from "./policyEmailUpdateTemplate";

interface IPolicyUpdatesListProps {
  customerId: number;
  customerPolicyId: number;
  policyNumber: string;
  customerName: string;
  customerEmail: string;
  effectiveDate: string;
  expiryDate: string;
}

export const INITIAL_POLICY_UPDATES: IPolicyUpdates = {
  policy_update_id: null,
  customer_policy_id: null,
  customer_id: null,
  policy_number: null,
  customer_name: null,
  customer_email: null,

  effective_date: null,
  expiry_date: null,
  province: null,
  gross_revenue: null,
  limit_of_liability: null,
  premium: null,
  tax: null,
  total_payable: null,
  link_for_payment: "accounts@aaxelinsurance.com",
  attachment: null,
  status: "ACTIVE",
};

export const PolicyUpdatesList: React.FC<IPolicyUpdatesListProps> = (props) => {
  const {
    customerId,
    customerPolicyId,
    customerEmail,
    policyNumber,
    customerName,
    effectiveDate,
    expiryDate,
  } = props;

  const { list, loading } = useSelector(
    (storeState: IStoreState) => storeState.policyCommon.policyUpdates
  );

  const dispatch = useDispatchWrapper();
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "-1"
  );
  const [openEdit, setOpenEdit] = React.useState<IPolicyUpdates | null>(null);

  const [openEmail, setOpenEmail] = React.useState<{
    key: string;
    data: IPolicyUpdates;
  } | null>(null);

  const {
    state: {
      dateState,
      searchState,
      tabs,
      columnsConfig,
      pagination,
      masterColumnResize,
      dense,
    },
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    onDenseToggle,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last28Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
  });

  const fetchList = () => {
    dispatch(
      fetchPolicyUpdatesListAsync(
        pagination.pageNumber,
        pagination.rowsInPerPage,
        pagination.apiFetchRowCount,
        dateState.dates,
        customerPolicyId,
        status
      )
    );
  };

  const handleComplete = () => {
    setOpenEdit(null);
    fetchList();
  };

  const handleShareOpen = (data: IPolicyUpdates) => () => {
    dispatch(
      getPolicyUpdateShareCode(
        data.policy_update_id as string,
        (isSuccess, key) => {
          if (isSuccess && key) {
            setOpenEmail({ key, data: data });
          }
        }
      )
    );
  };

  const policyUpdatesTableProps: IDataTableV2Props = {
    isPagination: false,
    isDataLoading: loading === LoadState.InProgress,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={[
                  { label: "All", value: "-1" },
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                  { label: "Submitted", value: "Submitted" },
                ]}
              />
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        onRowCellRender: (value, row: IPolicyUpdates) => {
          return (
            <StandardTableActions
              onEditClick={() => {
                setOpenEdit(row);
              }}
              emailLogs={{
                moduleName: MODULE_NAMES.POLICY_UPDATES,
                subModuleName: MODULE_NAMES.POLICY_UPDATES,
                moduleId: row.policy_update_id,
              }}

              otherActions={[
                {
                  icon: ShareOutlined,
                  onClick: handleShareOpen(row),
                  tooltip: "Share",
                },
              ]}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 20,
        onRowCellRender: (value, row) => {
          return <StatusRenderer status={row.status} />;
        },
      },

      {
        key: "customer_name",
        headerName: "Name Insured",
        fieldName: "customer_name",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "effective_date",
        headerName: "Effective Date",
        fieldName: "effective_date",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.DATE,
      },
      {
        key: "expiry_date",
        headerName: "Expiry Date",
        fieldName: "expiry_date",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.DATE,
      },
      {
        key: "province",
        headerName: "Province",
        fieldName: "province",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "gross_revenue",
        headerName: "Gross Revenue",
        fieldName: "gross_revenue",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
       
      },
      {
        key: "limit_of_liability",
        headerName: "Limits of Liability",
        fieldName: "limit_of_liability",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      
      },
      {
        key: "premium",
        headerName: "Premium",
        fieldName: "premium",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "tax",
        headerName: "Tax",
        fieldName: "tax",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "total_payable",
        headerName: "Total Payable",
        fieldName: "total_payable",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "created_by",
        headerName: "Created By",
        fieldName: "created_by_name",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "created_date",
        headerName: "Created Date",
        fieldName: "created_ts",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.DATE,
      },
      {
        key: "modified_by",
        headerName: "Modified By",
        fieldName: "modified_by_name",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "modified_date",
        headerName: "Modified Date",
        fieldName: "updated_ts",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.DATE,
      },
    ],
    rows: list,
    dense: dense,
    onDenseToggle: onDenseToggle,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
  };

  React.useEffect(() => {
    fetchList();
  }, [pagination.pageNumber, pagination.rowsInPerPage, status]);

  return (
    <>
      <PageTitleBar
        heading="Updates"
        rightHeading={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setOpenEdit({
                ...INITIAL_POLICY_UPDATES,
                customer_id: customerId,
                customer_policy_id: customerPolicyId,
                policy_number: policyNumber,
                customer_name: customerName,
                customer_email: customerEmail || "",
                effective_date: effectiveDate,
                expiry_date: expiryDate,
              });
            }}
          >
            Create
          </Button>
        }
      />
      <DataTableV2 {...policyUpdatesTableProps} />

      {openEdit && (
        <PolicyUpdatesRightPanel
          open={true}
          data={openEdit}
          onClose={() => setOpenEdit(null)}
          onComplete={handleComplete}
        />
      )}

      {openEmail && (
        <EmailComposeDialog
          open={true}
          toEmail={openEmail.data.customer_email || ""}
          subject={`Action Required Policy Update - ${openEmail.data.customer_name} (${openEmail.data.policy_number})`}
          onClose={() => setOpenEmail(null)}
          onComplete={() => {
            setOpenEmail(null);
          }}
          body={getEmailUpdateTemplate(openEmail.data, openEmail.key)}
          history={{
            module_name: MODULE_NAMES.POLICY_UPDATES,
            module_code: openEmail.data.policy_update_id as string
          }}
        />
      )}
    </>
  );
};
